/*
 * Rewritten in Vanilla JS
 *
 * Finds elements with the attribute "data-ghwp-pretty-name" and checks the
 * window's hash to click them and scroll to them. Useful for targeting specific
 * tabs or accordion elements when an ID is not practically usable.
 *
 * Works down to IE11
 *
 */

/**
 * @param {HashChangeEvent} ev
 */
const onHashChange = (ev) => {
    let newHash = window.location.hash;
    newHash = newHash.replace('#', '');
    if (!newHash) return;

    // /** @var {HTMLElement} */
    const tabOrCollapsibleButtons = document.querySelectorAll(
        `[data-ghwp-pretty-name="${newHash}"]`
    );

    for (let element of tabOrCollapsibleButtons) {
        if (!isElementVisible(element)) {
            // skip invisible elements
            continue;
        }

        if (element) {
            ev && ev.preventDefault();
            element = getRelevantElement(element);
            element.click();
            scrollToItem(element);
            return;
        }
    }

    checkNestedItem(newHash, ev);
};

/**
 * @param {string} hash
 * @param {?HashChangeEvent} hashChangeEvent
 */
const checkNestedItem = (hash, hashChangeEvent = null) => {
    const nestedItem = document.querySelector(
        `.ghwp-tab-panel #${hash}, .ghwp-showhide-panel #${hash}`
    );
    if (nestedItem) {
        const panel = nestedItem.closest(
            '.ghwp-tab-panel, .ghwp-showhide-panel'
        );
        if (!panel) return;
        let button = null;

        if (panel.matches('.ghwp-tab-panel')) {
            const buttonId = panel.getAttribute('aria-labelledby');
            button = document.getElementById(buttonId);
            if (!button) return;

            if (button.getAttribute('aria-selected') === 'true') {
                button = null;
            }
        } else if (panel.matches('.ghwp-showhide-panel')) {
            button = panel.parentElement.querySelector(
                '.ghwp-showhide-toggle button'
            );
            if (!button) return;

            if (panel.parentElement.classList.contains('is-open')) {
                button = null;
            }
        } else {
            return;
        }

        hashChangeEvent && hashChangeEvent.preventDefault();
        button && button.click();

        window.requestAnimationFrame(() => {
            scrollToItem(nestedItem);
        });
    }
};

/**
 * Returns the element that needs to be triggered, i.e. scrolled to and clicked.
 * Covers the special case where showhide / collapse has a frontend-generated
 * button to trigger its action; the same for the mobile view of tabs.
 *
 * @param {HTMLElement} originalElement
 * @return {HTMLElement}
 */
const getRelevantElement = (originalElement) => {
    let element = originalElement;

    const collapseButtonChild = originalElement.querySelector(
        'button.ghwp-showhide-toggle-button'
    );

    const mobileTabButtonChild =
        originalElement.classList.contains('ghwp-tab-title') &&
        originalElement.querySelector('button');

    if (collapseButtonChild) {
        element = collapseButtonChild;
    } else if (mobileTabButtonChild) {
        element = mobileTabButtonChild;
    }

    return element;
};

/**
 * Determines whether an element is visible to the user by checking its
 * boundingClientRect
 *
 * @param {HTMLElement} element
 * @return {boolean}
 */
const isElementVisible = (element) => {
    return element && element.offsetParent !== null;
};

/**
 * @param {HTMLElement} item
 * @return {number}
 */
const scrollToItem = (item) => {
    const scrollMargin = getScrollMarginTop(item);
    const desiredScrollPosition =
        getElementBodyOffsetRecursive(item) - scrollMargin;
    window.scrollTo({ top: desiredScrollPosition });
};

/**
 * @param {HTMLElement} item
 * @param {number} fallback
 * @return {number}
 */
const getScrollMarginTop = (item, fallback = 120) => {
    const scrollMarginProperty =
        window.getComputedStyle(item).scrollMarginTop || null;
    const scrollMarginNumber =
        (scrollMarginProperty && parseInt(scrollMarginProperty, 10)) || null;
    return scrollMarginNumber || fallback;
};

/**
 * Get the element's offset from the body element by following its offsetParents
 * right up to the top.
 *
 * @param {HTMLElement} element
 * @param {number} sum
 * @return {number}
 */
const getElementBodyOffsetRecursive = (element, sum = 0) => {
    /** @var {HTMLElement} */
    const parent = element.offsetParent;
    sum += element.offsetTop;
    if (parent !== document.body) {
        return getElementBodyOffsetRecursive(parent, sum);
    } else {
        return sum;
    }
};

/**
 * Init the module
 */
export const scrollToPrettyNameElementOrChild = () => {
    // Listen to hash change events
    window.addEventListener('hashchange', onHashChange);
    // run once after load
    setTimeout(onHashChange, 500);
};
